<template>
  <section
    class="receiving-type"
    :class="{ 'receiving-type-swipe': typeOptions.length > 1 && !loading }"
    :data-bet-type="column.betType"
    :aria-hidden="loading || preview ? 'true' : null"
    :style="{ minHeight: minimumHeight ? `${minimumHeight}px` : null }"
  >
    <div
      ref="heading"
      class="receiving-type-heading"
    >
      <div
        ref="title"
        class="receiving-type-title"
        :class="{ 'receiving-header-placeholder': nativeHeaders, 'receiving-mobile-title-placeholder': nativeHeaders && typeOptions.length }"
        :aria-hidden="nativeHeaders ? 'true' : null"
      >
        <ReceivingReportTypeTabs
          v-if="typeOptions.length && !nativeHeaders"
          :options="typeOptions"
          :value="column.betType"
          :show-counts="showTypeCounts"
          :loading="loading"
        />
        <h4 v-else-if="!nativeHeaders || !typeOptions.length"><ReceivingReportText :loading="loading">
          {{ $t(column.label) }}
        </ReceivingReportText></h4>
      </div>
      <dl class="receiving-type-summary receiving-swipe-area">
        <div><dt><ReceivingReportText :loading="loading">
          {{ $t('receivingReport.bet') }}
        </ReceivingReportText></dt><dd><ReceivingReportText :loading="loading">
          {{ money(column.acceptedAmount) }}
        </ReceivingReportText></dd></div>
        <div><dt><ReceivingReportText :loading="loading">
          {{ $t('receivingReport.commission') }}
        </ReceivingReportText></dt><dd><ReceivingReportText :loading="loading">
          {{ money(-column.commission) }}
        </ReceivingReportText></dd></div>
        <div :class="column.income < 0 ? 'report-text-danger' : 'report-text-success'">
          <dt><ReceivingReportText :loading="loading">
            {{ $t('receivingReport.income') }}
          </ReceivingReportText></dt><dd><ReceivingReportText :loading="loading">
            {{ money(column.income) }}
          </ReceivingReportText></dd>
        </div>
        <div class="report-text-danger">
          <dt><ReceivingReportText :loading="loading">
            {{ $t('receivingReport.maximum') }}
          </ReceivingReportText></dt><dd><ReceivingReportText :loading="loading">
            {{ money(column.maximumLoss) }}
          </ReceivingReportText></dd>
        </div>
      </dl>
      <div class="receiving-cap-row">
        <label :for="capId">
          <span><ReceivingReportText :loading="loading">{{ $t('receivingReport.cap') }}</ReceivingReportText></span>
          <small
            v-if="showCapSource"
            :id="capSourceId"
            class="report-text-secondary"
          ><ReceivingReportText :loading="loading">{{ $t(usesDefaultCap ? 'receivingReport.inherit' : 'receivingReport.customCap') }}</ReceivingReportText></small>
        </label>
        <b-form-input
          v-if="canEdit"
          :id="capId"
          :tabindex="preview ? -1 : null"
          :value="loading ? '' : draft"
          :disabled="saving || loading"
          :class="{ 'receiving-cap-dirty': dirty, 'receiving-skeleton-control b-skeleton-animate-fade': loading }"
          :aria-label="`${$t('receivingReport.cap')} ${$t(column.label)}`"
          :aria-describedby="showCapSource ? capSourceId : null"
          :placeholder="loading ? '' : capPlaceholder"
          type="number"
          inputmode="decimal"
          min="0"
          step="any"
          @input="$emit('cap', $event)"
        />
        <span
          v-else
          class="receiving-cap-value"
        ><ReceivingReportText :loading="loading">{{ column.capAmount == null ? $t('receivingReport.unlimited') : money(column.capAmount) }}</ReceivingReportText></span>
      </div>
      <div class="receiving-counts receiving-swipe-area">
        <span class="report-text-danger"><ReceivingReportText :loading="loading">{{ $t('receivingReport.full') }} <strong>{{ count(column.fullNumbers) }}</strong></ReceivingReportText></span>
        <span><ReceivingReportText :loading="loading">{{ $t('receivingReport.empty') }} <strong>{{ count(column.emptyNumbers) }}</strong></ReceivingReportText></span>
        <span><ReceivingReportText :loading="loading">{{ $t('receivingReport.all') }} <strong>{{ count(column.totalNumbers) }}</strong></ReceivingReportText></span>
      </div>
      <div
        ref="labels"
        class="receiving-labels-sticky"
        :class="{ 'receiving-header-placeholder': nativeHeaders }"
        aria-hidden="true"
      >
        <div class="receiving-grid-labels">
          <span>{{ $t('receivingReport.number') }}</span><span>{{ $t('receivingReport.accepted') }}</span><span>{{ $t('receivingReport.payout') }}</span>
        </div>
      </div>
    </div>
    <table
      class="receiving-numbers receiving-swipe-area"
      :aria-label="$t(column.label)"
      :aria-rowcount="column.numbers.length + 1"
    >
      <colgroup>
        <col class="receiving-number-column">
        <col class="receiving-accepted-column">
        <col class="receiving-payout-column">
      </colgroup>
      <thead class="sr-only">
        <tr><th scope="col">
          {{ $t('receivingReport.number') }}
        </th><th scope="col">
          {{ $t('receivingReport.accepted') }}
        </th><th scope="col">
          {{ $t('receivingReport.payout') }}
        </th></tr>
      </thead>
      <tbody>
        <tr
          v-if="windowRows.before"
          class="receiving-spacer"
          aria-hidden="true"
        >
          <td
            colspan="3"
            :style="{ height: `${windowRows.before}px` }"
          />
        </tr>
        <tr
          v-for="(item, index) in visibleNumbers"
          :key="item.number"
          :class="{ 'receiving-full-row': item.isFull }"
          :aria-rowindex="windowRows.start + index + 2"
          :data-number="item.number"
        >
          <td>
            <button
              v-if="isTodColumn"
              type="button"
              class="receiving-number-button"
              :tabindex="preview ? -1 : null"
              :aria-label="`${$t('receivingReport.groupDetails')} ${item.number}`"
              @click="$emit('details', item)"
            >
              {{ item.number }}
            </button>
            <strong
              v-else
              class="receiving-number"
            >{{ item.number }}</strong>
            <span
              v-if="item.isWinner"
              class="receiving-number-state"
            >
              <b-badge
                variant="light-success"
                class="report-status-win receiving-winner"
              >{{ $t('receivingReport.winner') }}</b-badge>
            </span>
          </td>
          <td>
            <span class="receiving-money">{{ money(item.acceptedAmount) }}</span>
            <span
              v-if="item.isFull"
              class="receiving-amount-state report-text-danger"
            >{{ $t('receivingReport.full') }}</span>
          </td>
          <td :class="item.potentialPayout < 0 ? 'report-text-success' : 'report-text-danger'">
            <span class="receiving-money">{{ money(item.potentialPayout) }}</span>
          </td>
        </tr>
        <tr
          v-if="windowRows.after"
          class="receiving-spacer"
          aria-hidden="true"
        >
          <td
            colspan="3"
            :style="{ height: `${windowRows.after}px` }"
          />
        </tr>
        <tr v-if="!column.numbers.length">
          <td
            colspan="3"
            class="receiving-empty"
          >
            <ReceivingReportText :loading="loading">
              {{ column.numberCount > 0 ? emptyMessage : $t('receivingReport.noNumbers') }}
            </ReceivingReportText>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<script>
import { BBadge, BFormInput } from 'bootstrap-vue'
import { money } from '@/utils/poyReport'
import { capInput, isReceivingTodType, receivingWindow } from '@/utils/receivingReport'
import ReceivingReportText from './ReceivingReportText.vue'
import ReceivingReportTypeTabs from './ReceivingReportTypeTabs.vue'

export default {
  components: {
    BBadge, BFormInput, ReceivingReportText, ReceivingReportTypeTabs,
  },
  props: {
    column: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    preview: { type: Boolean, default: false },
    draft: { type: String, default: '' },
    dirty: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    showCapSource: { type: Boolean, default: true },
    saving: { type: Boolean, default: false },
    scrollTop: { type: Number, default: 0 },
    viewportHeight: { type: Number, default: 600 },
    nativeHeaders: { type: Boolean, default: false },
    minimumHeight: { type: Number, default: 0 },
    typeOptions: { type: Array, default: () => [] },
    showTypeCounts: { type: Boolean, default: false },
    emptyMessage: { type: String, default: '' },
  },
  data() {
    return {
      headingHeight: 280, titleHeight: 44, labelsHeight: 38, headingObserver: null,
    }
  },
  computed: {
    isTodColumn() { return isReceivingTodType(this.column.betType) },
    capId() { return `receiving-cap-${this.preview ? 'preview-' : ''}${this.column.betType}` },
    capSourceId() { return `receiving-cap-${this.preview ? 'preview-' : ''}source-${this.column.betType}` },
    usesDefaultCap() {
      if (!this.canEdit) return !this.column.hasCapOverride
      // A saved unlimited override also has an empty input; keep its explicit source.
      return this.draft.trim() === '' && (this.dirty || !this.column.hasCapOverride || this.column.capAmount != null)
    },
    capPlaceholder() {
      if (!this.usesDefaultCap) return this.$t('receivingReport.unlimited')
      const hasDefault = Object.prototype.hasOwnProperty.call(this.column, 'defaultCapAmount')
      // Older report APIs cannot tell us the fallback beneath an existing override.
      if (!hasDefault && this.column.hasCapOverride) return this.$t('receivingReport.inherit')
      const value = hasDefault ? this.column.defaultCapAmount : this.column.capAmount
      return capInput(value) || this.$t('receivingReport.unlimited')
    },
    summaryHeight() { return Math.max(0, this.headingHeight - this.titleHeight - this.labelsHeight) },
    windowRows() { return receivingWindow(this.column.numbers.length, this.scrollTop, this.viewportHeight, this.headingHeight, this.summaryHeight) },
    visibleNumbers() { return this.column.numbers.slice(this.windowRows.start, this.windowRows.end) },
  },
  mounted() {
    this.measureHeading()
    if (typeof ResizeObserver !== 'undefined') {
      this.headingObserver = new ResizeObserver(this.measureHeading)
      this.headingObserver.observe(this.$refs.heading)
      this.headingObserver.observe(this.$refs.title)
      this.headingObserver.observe(this.$refs.labels)
    }
    window.addEventListener('resize', this.measureHeading)
  },
  beforeDestroy() {
    if (this.headingObserver) this.headingObserver.disconnect()
    window.removeEventListener('resize', this.measureHeading)
  },
  methods: {
    money,
    count: value => Number(value || 0).toLocaleString('en-US'),
    measureHeading() {
      if (!this.$refs.heading) return
      this.headingHeight = this.$refs.heading.getBoundingClientRect().height
      this.titleHeight = this.$refs.title.getBoundingClientRect().height
      this.labelsHeight = this.$refs.labels.getBoundingClientRect().height
    },
  },
}
</script>
